/**
 * Staff Users Hooks
 *
 * Provides hooks for fetching staff users (mechanics, etc.) with TanStack Query.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { sortStaffByDisplayName } from '@/utils/staffNames';

export interface StaffUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
  roleId?: string | null;
  roleName?: string | null;
  isActive: boolean;
  /** Counts toward workshop capacity (#3120). */
  isSchedulable?: boolean;
  preferredLanguage: string;
  whatsappNumber: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface StaffUsersResponse {
  users: StaffUser[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface StaffUserFilters {
  role?: 'admin' | 'manager' | 'mechanic' | 'reception';
  search?: string;
  isActive?: boolean;
}

/**
 * Hook to fetch staff users with optional filters
 */
export function useStaffUsers(filters: StaffUserFilters = {}) {
  return useQuery({
    queryKey: ['staffUsers', filters],
    queryFn: () =>
      api.get<StaffUsersResponse>('/users', {
        params: {
          role: filters.role,
          search: filters.search,
          isActive: filters.isActive,
          pageSize: 100, // Get all for dropdown use
        },
      }),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch performers: active users with the HR page's capacity flag
 * on, whatever their role, so a foreman who also turns wrenches is listed
 * (#3658).
 */
export function useMechanics() {
  return useQuery({
    queryKey: ['staffUsers', { schedulable: true }],
    queryFn: () =>
      api.get<StaffUsersResponse>('/users', {
        params: {
          schedulable: true,
          isActive: true,
          pageSize: 100,
        },
      }),
    staleTime: 60 * 1000,
    select: (data) => sortStaffByDisplayName(data.users),
  });
}

// ─── Mechanics with skills ─────────────────────────────────────

export interface MechanicSkillInfo {
  skillId: string;
  skillName: string;
  skillType: string;
  proficiencyLevel: string;
  /** Certification expiry (ISO). Null/absent means it never expires. */
  expiresAt?: string | null;
}

export interface MechanicWithSkills extends StaffUser {
  skills: MechanicSkillInfo[];
}

interface MechanicsWithSkillsResponse {
  users: MechanicWithSkills[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Hook to fetch mechanics with their skills included
 */
export function useMechanicsWithSkills() {
  return useQuery({
    queryKey: ['staffUsers', { schedulable: true, includeSkills: true }],
    queryFn: () =>
      api.get<MechanicsWithSkillsResponse>('/users', {
        params: {
          schedulable: true,
          isActive: true,
          includeSkills: true,
          pageSize: 100,
        },
      }),
    staleTime: 60 * 1000,
    select: (data) => sortStaffByDisplayName(data.users),
  });
}
